import React from 'react';
import { NavLink } from 'react-router-dom';
import { ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  getBaseListItemStyles,
  getNavLinkListItemStyles,
  getListItemIconStyles,
  listItemTextStyles,
  listItemTextTypographyProps,
  shouldShowText,
} from './navigationStyles';

/**
 * NavigationItem component for rendering a single navigation item in the sidebar
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - Navigation item data
 * @param {boolean} props.miniDrawer - Whether the drawer is in mini mode
 * @param {boolean} props.isDesktop - Whether the current view is desktop
 * @param {Function} props.onClick - Optional click handler
 * @returns {JSX.Element} NavigationItem component
 */
const NavigationItem = ({
  item,
  miniDrawer,
  isDesktop,
  onClick
}) => {
  const theme = useTheme();
  const { path, label, icon: Icon } = item;

  // For items that should be rendered as a div instead of NavLink (e.g., for handlers)
  if (item.component === 'div' || !path || path === '#') {
    return (
      <ListItem
        onClick={onClick}
        sx={{
          ...getBaseListItemStyles(theme),
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          ...(miniDrawer && isDesktop && {
            justifyContent: 'center',
          }),
        }}
      >
        <ListItemIcon
          sx={{
            ...getListItemIconStyles({ miniDrawer, isDesktop }),
            minWidth: miniDrawer ? 'auto' : 40,
          }}
        >
          <Icon />
        </ListItemIcon>
        {shouldShowText(isDesktop, miniDrawer) && (
          <ListItemText
            sx={listItemTextStyles}
            primaryTypographyProps={listItemTextTypographyProps}
            primary={label}
          />
        )}
      </ListItem>
    );
  }

  // Regular navigation item with NavLink
  return (
    <ListItem
      component={NavLink}
      to={path}
      onClick={onClick}
      sx={{
        ...getNavLinkListItemStyles(theme, { miniDrawer, isDesktop }),
        display: (item.mobileOnly && isDesktop) || (item.desktopOnly && !isDesktop)
          ? 'none'
          : 'flex',
      }}
    >
      <ListItemIcon
        sx={{
          ...getListItemIconStyles({ miniDrawer, isDesktop }),
          ...(!(miniDrawer && isDesktop) && {
            ml: miniDrawer ? 0.5 : 0,
          }),
        }}
      >
        <Icon />
      </ListItemIcon>
      {shouldShowText(isDesktop, miniDrawer) && (
        <ListItemText
          sx={listItemTextStyles}
          primaryTypographyProps={listItemTextTypographyProps}
          primary={label}
        />
      )}
    </ListItem>
  );
};

export default NavigationItem;
